/* eslint react/prop-types: 0 */
import PropTypes from 'prop-types';
import React from 'react';
import ReactCollapse from 'react-collapse';

import { LocaleService, ContentZonesService } from '@sm360/widget-commons-core-services';
import { ModelYear } from '@sm360/widget-commons-vehicle';

import { MODEL_TRIMS } from 'build-and-quote/constants/sections.js';
import ModelTrimFeatures from 'build-and-quote/components/ModelTrims/ModelTrimFeatures.jsx';
import ModelTrimImages from 'build-and-quote/components/ModelTrims/ModelTrimImages.jsx';
import ModelTrimColours from 'build-and-quote/components/ModelTrims/ModelTrimColours.jsx';
import CabSizes from 'build-and-quote/components/ModelTrims/components/CabSizes/CabSizes.jsx';
import Trims from 'build-and-quote/components/ModelTrims/components/Trims/Trims.jsx';

import { getSectionAdditionalClasses } from 'build-and-quote/services/SectionClassesUtils/SectionClassesUtils.js';

import 'build-and-quote/components/ModelTrims/modelTrims.scss';
import 'build-and-quote/components/ModelTrims/modelTrimSelection.scss';

/* eslint jsx-a11y/no-static-element-interactions: 0 */

const ModelTrims = ({
    currentSection,
    imageServer,
    modelYear,
    selectBoxSize,
    selectCabSize,
    selectedModel,
    selectExteriorColour,
    selectInteriorColour,
    selectModelTrim,
    hasAdobeTagging,
}) => {
    const availableCabSizes = modelYear.cabSizes;
    const availableBoxSizes = modelYear.boxSizes;
    const selectedCabSize = modelYear.getSelectedCabSize();
    const selectedBoxSize = modelYear.getSelectedBoxSize();

    const availableModelTrims = modelYear.getAvailableModelTrims().toArray();
    const selectedModelTrim = modelYear.getSelectedModelTrim();
    const selectedConfiguration = modelYear.getSelectedOptionsPackageConfiguration();

    const availableExteriorColoursList = selectedConfiguration ? selectedConfiguration.exteriorColours : undefined;

    const optionsPackageImageUrls = availableExteriorColoursList
        ? availableExteriorColoursList.filter((colour) => colour.selected).first()
        : undefined;

    const exteriorColourImageServerDomain = imageServer
        ? imageServer.imageServerDomain + imageServer.imageExteriorColorsRelative
        : undefined;

    const interiorColourImageServerDomain = imageServer
        ? imageServer.imageServerDomain + imageServer.imageInteriorColorsRelative
        : undefined;

    const sectionOpened = currentSection === MODEL_TRIMS.value;

    const availableModelTrimColoursSection = (
        <ModelTrimColours
            optionsPackageConfiguration={selectedConfiguration}
            exteriorColourImageServerDomain={exteriorColourImageServerDomain}
            interiorColourImageServerDomain={interiorColourImageServerDomain}
            onExteriorColourClick={(exteriorColour) => selectExteriorColour(exteriorColour)}
            onInteriorColourClick={(interiorColour) => selectInteriorColour(interiorColour)}
        />
    );

    const availableModelTrimImagesSection = (
        <ModelTrimImages
            optionsPackageConfiguration={selectedConfiguration}
            optionsPackageImageUrls={optionsPackageImageUrls}
            imageServerDomain={imageServer}
        />
    );

    const availableModelTrimFeaturesSection = <ModelTrimFeatures selectedModelTrim={selectedModelTrim} />;

    const sectionClasses = ['section-model-trim', 'bloc-container', 'section-background', 'clearfix'];
    const sectionAdditionalClasses = getSectionAdditionalClasses(
        sectionClasses,
        sectionOpened,
        currentSection,
        MODEL_TRIMS.value
    ).join(' ');

    const genericTitle = ContentZonesService.getContentZone('widget.build.and.buy', 'vehicle.title.text')
        ? ContentZonesService.getContentZone('widget.build.and.buy', 'vehicle.title.text').getValue()
        : LocaleService.t('modelYearSection.title');

    const sectionTitle = LocaleService.t('SECTIONS.TITLE.GENERAL', {
        title: genericTitle,
        model: `${selectedModel.model}`,
    });
    const sectionSubtitle = LocaleService.t('modelTrimSection.subtitle');

    const background = `url(${ContentZonesService.getContentZone(
        'widget.build.and.buy',
        'style.background.img'
    ).getValue()})`;

    return (
        <ReactCollapse isOpened={sectionOpened} springConfig={{ stiffness: 140, damping: 26 }}>
            <section className={sectionAdditionalClasses} style={{ backgroundImage: background }}>
                <div className="bloc-container-wrapper">
                    <h3 className="sr-subtitle-1 is-centered">{sectionTitle}</h3>
                    <h4 className="sr-title-1 is-centered">{sectionSubtitle}</h4>
                </div>
                <CabSizes
                    availableCabSizes={availableCabSizes}
                    onCabSelect={selectCabSize}
                    selectedCabSize={selectedCabSize}
                    availableBoxSizes={availableBoxSizes}
                    onBoxSelect={selectBoxSize}
                    selectedBoxSize={selectedBoxSize}
                />
                <Trims
                    availableModelTrims={availableModelTrims}
                    onSelect={selectModelTrim}
                    selectedTrim={selectedModelTrim}
                    hasAdobeTagging={hasAdobeTagging}
                />

                <div className="bloc-container clearfix">{availableModelTrimImagesSection}</div>

                {availableModelTrimColoursSection}

                <div className="bloc-container clearfix">
                    <div className="bloc-container-wrapper">{availableModelTrimFeaturesSection}</div>
                </div>
            </section>
        </ReactCollapse>
    );
};

ModelTrims.defaultProps = {
    selectedModel: [],
    hasAdobeTagging: false,
};

ModelTrims.propTypes = {
    currentSection: PropTypes.number.isRequired,
    imageServer: PropTypes.shape({
        imageServerDomain: PropTypes.string.isRequired,
        imageServerFullSize: PropTypes.string.isRequired,
    }).isRequired,
    modelYear: PropTypes.instanceOf(ModelYear.ModelYearModel).isRequired,
    selectBoxSize: PropTypes.func.isRequired,
    selectCabSize: PropTypes.func.isRequired,
    selectExteriorColour: PropTypes.func.isRequired,
    selectInteriorColour: PropTypes.func.isRequired,
    selectedModel: PropTypes.shape({
        make: PropTypes.string.isRequired,
        model: PropTypes.string.isRequired,
    }),
    selectModelTrim: PropTypes.func.isRequired,
    hasAdobeTagging: PropTypes.bool,
};

ModelTrims.displayName = 'ModelTrims';

export default ModelTrims;
